import { TAU, WHITE, circle, glow, rim } from '../paint';
import type { Ctx, Piece } from '../paint';
import { REFERENCE_RADIUS } from '../textures-looks';

export const UNIT = 2;

const SUN = '#ffc94d';
export const MOON = '#b9c2ff';
const NIGHT = '#2a2f78';
const STAR = '#fff6dc';
const SHINE = '#fff3c0';
const HAIR = '#3a2414';
const OUT = 'rgba(4,6,18,.9)';

const PONCHO = { width: 10, height: 14 } as const;
export const HEADDRESS = { along: -1, radius: 11, rays: 9, rayTip: 15.5 } as const;
export const HEAD = { along: 3, radius: 6, hair: 4.5, hairAlong: 2 } as const;
export const HANDS = { along: 9, across: 12.5, sway: 1.6 } as const;
export const CRESCENT = { radius: 8, glow: 15 } as const;
const DIAL = { radius: 8, sun: 3, moon: 4.8 } as const;

function units(draw: (ctx: Ctx) => void): (ctx: Ctx) => void {
  return (ctx) => {
    ctx.save();
    ctx.scale(UNIT, UNIT);
    draw(ctx);
    ctx.restore();
  };
}

function fillRim(ctx: Ctx, fill: string, width: number): void {
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.lineWidth = width;
  ctx.strokeStyle = OUT;
  ctx.lineJoin = 'round';
  ctx.stroke();
}

function ponchoPath(ctx: Ctx): void {
  ctx.beginPath();
  ctx.ellipse(0, 0, PONCHO.width, PONCHO.height, 0, 0, TAU);
}

function ponchoSun(ctx: Ctx): void {
  ctx.save();
  ponchoPath(ctx);
  ctx.clip();
  ctx.fillStyle = WHITE;
  ctx.fillRect(-PONCHO.width - 2, -PONCHO.height - 2, 2 * PONCHO.width + 4, PONCHO.height + 2);
  ctx.strokeStyle = 'rgba(0,0,0,.28)';
  ctx.lineWidth = 1;
  ctx.lineCap = 'round';
  for (let index = 0; index < 5; index += 1) {
    ctx.beginPath();
    ctx.moveTo(-8 + index * 4, -1);
    ctx.lineTo(-6 + index * 4, -13);
    ctx.stroke();
  }
  ctx.restore();
}

function ponchoNight(ctx: Ctx): void {
  ctx.save();
  ponchoPath(ctx);
  ctx.clip();
  ctx.fillStyle = NIGHT;
  ctx.fillRect(-PONCHO.width - 2, 0, 2 * PONCHO.width + 4, PONCHO.height + 2);
  for (let index = 0; index < 4; index += 1) {
    circle(ctx, 0.9, -6 + index * 4, 5 + (index % 2) * 4);
    ctx.fillStyle = STAR;
    ctx.fill();
  }
  ctx.restore();
  ponchoPath(ctx);
  ctx.lineWidth = 2;
  ctx.strokeStyle = OUT;
  ctx.stroke();
}

function silhouette(ctx: Ctx): void {
  ponchoPath(ctx);
  ctx.fillStyle = WHITE;
  ctx.fill();
  circle(ctx, HEADDRESS.radius, HEADDRESS.along);
  ctx.fill();
}

export function crescentPath(ctx: Ctx, radius: number): void {
  const offset = radius * 0.55;
  const inner = radius * 0.85;
  const horn = Math.acos(
    (radius * radius + offset * offset - inner * inner) / (2 * radius * offset),
  );
  const back = Math.atan2(radius * Math.sin(horn), radius * Math.cos(horn) - offset);
  ctx.beginPath();
  ctx.arc(0, 0, radius, horn, TAU - horn);
  ctx.arc(offset, 0, inner, -back, back, true);
  ctx.closePath();
}

export function sunPath(ctx: Ctx, radius: number, outline: number): void {
  for (let index = 0; index < 12; index += 1) {
    const angle = (index * TAU) / 12;
    ctx.beginPath();
    ctx.moveTo(Math.cos(angle - 0.13) * radius * 1.05, Math.sin(angle - 0.13) * radius * 1.05);
    ctx.lineTo(Math.cos(angle) * radius * 1.65, Math.sin(angle) * radius * 1.65);
    ctx.lineTo(Math.cos(angle + 0.13) * radius * 1.05, Math.sin(angle + 0.13) * radius * 1.05);
    ctx.closePath();
    fillRim(ctx, SUN, outline * 0.6);
  }
  circle(ctx, radius);
  fillRim(ctx, SUN, outline);
  circle(ctx, radius * 0.6);
  ctx.lineWidth = outline * 0.8;
  ctx.strokeStyle = SHINE;
  ctx.stroke();
}

function headdress(ctx: Ctx): void {
  const { radius, rays, rayTip } = HEADDRESS;
  for (let index = 0; index < rays; index += 1) {
    const angle = -Math.PI / 2 + ((index + 0.5) * Math.PI) / rays;
    ctx.beginPath();
    ctx.moveTo(Math.cos(angle - 0.15) * 10, Math.sin(angle - 0.15) * 10);
    ctx.lineTo(Math.cos(angle) * rayTip, Math.sin(angle) * rayTip);
    ctx.lineTo(Math.cos(angle + 0.15) * 10, Math.sin(angle + 0.15) * 10);
    ctx.closePath();
    fillRim(ctx, SUN, 0.8);
  }
  ctx.beginPath();
  ctx.arc(0, 0, radius, -Math.PI / 2, Math.PI / 2);
  ctx.closePath();
  fillRim(ctx, SUN, 1.4);
  ctx.beginPath();
  ctx.arc(0, 0, radius, Math.PI / 2, Math.PI * 1.5);
  ctx.closePath();
  fillRim(ctx, NIGHT, 1.4);
  ctx.save();
  ctx.translate(-6, -2.5);
  ctx.rotate(Math.PI / 2);
  crescentPath(ctx, 3.4);
  fillRim(ctx, MOON, 1);
  ctx.restore();
  for (const [x, y, r] of [
    [-3.5, 6, 0.9],
    [-7.5, 4, 0.7],
    [-4, -8, 0.7],
  ] as const) {
    circle(ctx, r, x, y);
    ctx.fillStyle = STAR;
    ctx.fill();
  }
  ctx.beginPath();
  ctx.arc(0, 0, 8, -1.2, 1.2);
  ctx.lineWidth = 0.9;
  ctx.lineCap = 'round';
  ctx.strokeStyle = SHINE;
  ctx.stroke();
}

function head(ctx: Ctx): void {
  circle(ctx, HEAD.radius);
  ctx.fillStyle = WHITE;
  ctx.fill();
  rim(ctx, 1.6);
}

function hair(ctx: Ctx): void {
  circle(ctx, HEAD.hair);
  ctx.fillStyle = HAIR;
  ctx.fill();
}

function hand(ctx: Ctx): void {
  circle(ctx, 3.2);
  ctx.fillStyle = WHITE;
  ctx.fill();
  rim(ctx, 1.3);
}

function crescent(ctx: Ctx): void {
  crescentPath(ctx, CRESCENT.radius);
  fillRim(ctx, MOON, 1);
}

function dialFace(ctx: Ctx): void {
  circle(ctx, DIAL.radius);
  ctx.fillStyle = 'rgba(6,10,28,.82)';
  ctx.fill();
  ctx.lineWidth = 0.8;
  ctx.strokeStyle = 'rgba(246,236,210,.35)';
  ctx.stroke();
}

function dialSun(ctx: Ctx): void {
  sunPath(ctx, DIAL.sun, 0.5);
}

function dialMoon(ctx: Ctx): void {
  ctx.rotate(-0.5);
  crescentPath(ctx, DIAL.moon);
  fillRim(ctx, MOON, 0.6);
}

function soft(width: number, height: number): Piece {
  return [
    96,
    96,
    REFERENCE_RADIUS,
    (ctx) => {
      glow(ctx, WHITE, 8);
      ctx.fillStyle = WHITE;
      ctx.beginPath();
      ctx.ellipse(0, 0, width, height, 0, 0, TAU);
      ctx.fill();
    },
  ];
}

export const CIRCADIEN_PIECES = {
  silhouette: [52, 64, REFERENCE_RADIUS, units(silhouette)],
  ponchoSun: [48, 64, REFERENCE_RADIUS, units(ponchoSun)],
  ponchoNight: [48, 64, REFERENCE_RADIUS, units(ponchoNight)],
  headdress: [68, 68, REFERENCE_RADIUS, units(headdress)],
  head: [30, 30, REFERENCE_RADIUS, units(head)],
  hair: [22, 22, REFERENCE_RADIUS, units(hair)],
  hand: [16, 16, REFERENCE_RADIUS, units(hand)],
  crescent: [38, 38, REFERENCE_RADIUS, units(crescent)],
  dialFace: [38, 38, REFERENCE_RADIUS, units(dialFace)],
  dialSun: [26, 26, REFERENCE_RADIUS, units(dialSun)],
  dialMoon: [26, 26, REFERENCE_RADIUS, units(dialMoon)],
  shadow: soft(22, 28),
} satisfies Record<string, Piece>;
